import { extensionFetchJson } from '../extensionClient';
import type { MarketDataProvider } from '../MarketDataProvider';
import type { Candle, CandleResponse, MarketDataRequest, MarketMeta } from '../../types/market';

interface YahooChartResult {
  meta?: {
    symbol?: string;
    currency?: string;
    exchangeName?: string;
    instrumentType?: string;
    regularMarketPrice?: number;
    chartPreviousClose?: number;
    previousClose?: number;
    exchangeTimezoneName?: string;
  };
  timestamp?: number[];
  indicators?: {
    quote?: Array<{
      open?: Array<number | null>;
      high?: Array<number | null>;
      low?: Array<number | null>;
      close?: Array<number | null>;
      volume?: Array<number | null>;
    }>;
  };
}

interface YahooChartEnvelope {
  chart?: {
    result?: YahooChartResult[] | null;
    error?: { code?: string; description?: string } | null;
  };
}

export class YahooFinanceProvider implements MarketDataProvider {
  readonly id = 'yahoo';
  readonly name = 'Yahoo Finance';

  async getCandles(request: MarketDataRequest): Promise<CandleResponse> {
    const symbol = normalizeSymbol(request.symbol);
    const payload = await fetchYahooChart(symbol, request);
    const chartError = payload.chart?.error;
    if (chartError) throw new Error(chartError.description || chartError.code || 'Yahoo chart error');

    const result = payload.chart?.result?.[0];
    if (!result) throw new Error(`Yahoo returned no chart data for ${symbol}`);

    const candles = parseYahooCandles(result);
    if (!candles.length) throw new Error(`Yahoo returned no usable candles for ${symbol}`);

    const meta: MarketMeta = {
      symbol: result.meta?.symbol || symbol,
      currency: result.meta?.currency,
      exchangeName: result.meta?.exchangeName,
      instrumentType: result.meta?.instrumentType,
      regularMarketPrice: result.meta?.regularMarketPrice,
      previousClose: result.meta?.previousClose,
      timezone: result.meta?.exchangeTimezoneName
    };

    return {
      provider: this.id,
      symbol,
      interval: request.interval,
      range: request.range,
      candles,
      meta,
      fetchedAt: Date.now()
    };
  }
}

function parseYahooCandles(result: YahooChartResult): Candle[] {
  const timestamps = result.timestamp ?? [];
  const quote = result.indicators?.quote?.[0];
  if (!quote) return [];

  const out: Candle[] = [];
  for (let i = 0; i < timestamps.length; i += 1) {
    const open = quote.open?.[i];
    const high = quote.high?.[i];
    const low = quote.low?.[i];
    const close = quote.close?.[i];
    const volume = quote.volume?.[i] ?? 0;
    if (open == null || high == null || low == null || close == null) continue;
    if (![open, high, low, close].every(Number.isFinite)) continue;
    out.push({
      time: timestamps[i],
      open,
      high,
      low,
      close,
      volume: Number.isFinite(volume) ? volume : 0
    });
  }
  return out;
}

async function fetchYahooChart(symbol: string, request: MarketDataRequest): Promise<YahooChartEnvelope> {
  const hosts = ['query1.finance.yahoo.com', 'query2.finance.yahoo.com'];
  let lastError: Error | undefined;

  for (const host of hosts) {
    const url = new URL(`https://${host}/v8/finance/chart/${encodeURIComponent(symbol)}`);
    url.searchParams.set('interval', request.interval);
    url.searchParams.set('range', request.range);
    url.searchParams.set('includePrePost', request.extendedHours ? 'true' : 'false');
    url.searchParams.set('events', 'div,splits');

    try {
      const response = await extensionFetchJson<YahooChartEnvelope>(url.toString(), {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (response.status < 200 || response.status >= 300) {
        lastError = new Error(`Yahoo ${host} request failed (${response.status} ${response.statusText})`);
        continue;
      }
      return response.body;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
    }
  }
  throw lastError ?? new Error('Yahoo request failed');
}

function normalizeSymbol(symbol: string): string {
  const normalized = symbol.trim().toUpperCase();
  if (!/^[A-Z0-9.^=_-]{1,32}$/.test(normalized)) throw new Error('Invalid Yahoo symbol format');
  return normalized;
}
